/**
 * track — thin guarded wrapper over `window.pendo.track` for the payment funnel
 * (Phase 5.3, AC#14 / RESEARCH §"Observability Plan").
 *
 * Pendo's agent (loaded lazily by `loadPendoScript`) exposes `window.pendo.track`.
 * No `track()` helper existed (analytics/ had only loaders.ts), so this is the
 * single funnel-event entry point. It MUST no-op safely when Pendo is absent
 * (dev, non-prod hosts where the agent never injects, or before the agent loads)
 * — analytics must never throw into the payment path.
 *
 * Funnel events (RESEARCH §"Observability Plan"):
 *   payment_modal_opened → payment_method_selected → ach_consent_confirmed →
 *   payment_attempted (tokenize-start = intent denominator) →
 *   payment_succeeded / payment_failed (with reason); plus
 *   save_method_prompted / save_method_saved / save_method_skipped.
 */

/** Canonical payment-funnel event names — keeps call sites grep-able + typo-safe. */
export type PaymentFunnelEvent =
  | 'payment_modal_opened'
  | 'payment_method_selected'
  | 'ach_consent_confirmed'
  | 'payment_attempted'
  | 'payment_succeeded'
  | 'payment_failed'
  | 'save_method_prompted'
  | 'save_method_saved'
  | 'save_method_skipped';

/**
 * Fire a Pendo funnel event. No-ops when `window.pendo` (or its `.track`) is
 * absent so analytics never breaks the payment flow. Swallows any error the
 * agent throws — a telemetry failure must not surface to the customer.
 */
export function track(
  event: PaymentFunnelEvent | (string & {}),
  props?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  const pendo = window.pendo;
  if (!pendo || typeof pendo.track !== 'function') return;
  try {
    pendo.track(event, props);
  } catch {
    // Telemetry is best-effort — never let a Pendo failure reach the user.
  }
}
